import React, { useEffect, useState } from "react";
import { useSubheader } from "@metronic/layout";
import { Alert, Accordion, Button, Col, Form, Row } from "react-bootstrap";
import { Redirect, useHistory } from "react-router-dom";
import { HospitalData } from "@components/panels/hospital-data/hospital-data";
import { InssuredData } from "@components/panels/insured-data/inssured-data";
import { FamilyData } from "@components/panels/family-data/family-data";
import { MedicDataTreating } from "@components/panels/medic-treating-data/medic-data-treating";
import { InsuranceData } from "@components/panels/insurance-data/insurance-data";
import { Opinion } from "@components/panels/dictum/dictum";
import { MedicalFees } from "@components/panels/medical-fee/medical-fees";
import AuthLetters from "@components/panels/auth-letter/auth-letters";
import { Documentation } from "@components/panels/documentation/documentation";
import { Binnacle } from "@components/panels/binnacle/binnacle";
import { ConciergeFollowUp } from "@components/panels/concierge-follow-up/concierge-follow-up";
import CaseHeader from "@components/panels/case-header/case-header";
import BlockCaseContext from "@contexts/block-case-context";
import { useCommentValidation } from "@contexts/comment-validation-context";
import WithCommentValidation from "@components/shared/with-comment-validation";
import { useAutomaticCommentSave } from "@hooks/use-automatic-comment-save";
import { getBySlug, update } from "@services/hospitalization";
import { storeCaseMovement } from "@services/mc-movements";
import { isCaseInCorrectModule } from "@utils/check-module-status";
import { getMexicoTimestamp } from "@utils/date-utils";
import { CASE_STATUS } from "@constants/case-statuses";

const ConciergeDetails = (props) => {
  const [caseRecord, setCaseRecord] = useState({});
  const [totalLetters, setTotalLetters] = useState(0.0);
  const [shouldBlockCase, setShouldBlockCase] = useState(true);
  const [reprocessReason, setReprocessReason] = useState("");

  const subheader = useSubheader();
  const history = useHistory();
  const { validateCommentRequired } = useCommentValidation();
  const { saveCommentAutomatically } = useAutomaticCommentSave({ caseData: caseRecord });

  const { slug } = props.match.params;

  useEffect(() => {
    getBySlug(slug).then((response) => setCaseRecord(response.data));

    subheader.setTitle("Concierge");
  }, [slug]);

  /** Cambia el caso de estacion y deja el movimiento en tra_mc_movements. */
  const moveCase = async (changes, destination, motive = "") => {
    if (!validateCommentRequired()) {
      return;
    }

    await saveCommentAutomatically();
    await storeCaseMovement(caseRecord, `Concierge a ${destination}${motive ? `: ${motive}` : ""}`).catch(() => {});
    update(changes, caseRecord.id).then(() => history.push("/concierge"));
  };

  const onClickSendStorePatient = () =>
    moveCase({ caseStatusId: CASE_STATUS.STORE_PATIENT, feaapaciente: getMexicoTimestamp() }, "Alta Paciente");

  /** Regresar el caso a otra estacion exige el motivo. */
  const sendBack = (destination, buildChanges) => {
    const motive = reprocessReason.trim();
    if (motive === "") {
      alert("Debe capturar el motivo del reproceso");
      return;
    }
    moveCase(buildChanges(motive, getMexicoTimestamp()), destination, motive);
  };

  const onClickSendMedicalOpinion = () =>
    sendBack("Dictamen Medico", (motive, timestamp) => ({
      caseStatusId: CASE_STATUS.MEDICAL_OPINION,
      dictumReason: motive,
      feadictamen: timestamp,
    }));

  const onClickSendControlDesk = () =>
    sendBack("Mesa de Control", (motive, timestamp) => ({
      caseStatusId: CASE_STATUS.CONTROL_DESK,
      reasonMC: motive,
      feamesaControl: timestamp,
    }));

  const onClickSendCostControl = () =>
    sendBack("Control de Costos", (motive, timestamp) => ({
      caseStatusId: CASE_STATUS.COST_CONTROL,
      feaccostos: timestamp,
    }));

  if (!isCaseInCorrectModule(caseRecord, [CASE_STATUS.CONCIERGE])) {
    return <Redirect to="/error/unauthorized" />;
  }

  return (
    <>
      <Alert
        variant="success"
        className="text-center"
        style={{ fontSize: "16px" }}
      >
        Concierge
      </Alert>

      <CaseHeader caseRecord={caseRecord} requiresTabulationSlot={<></>} />

      <BlockCaseContext.Provider value={[shouldBlockCase, setShouldBlockCase]}>
        <Accordion className="mt-5" defaultActiveKey="12">
          <HospitalData
            icon="fa-search"
            eventKey="0"
            data={caseRecord}
            disabledAll={true}
            bindSubmitForm={() => {}}
          />
          <InssuredData
            icon="fa-search"
            eventKey="2"
            data={caseRecord}
            disabledAll={true}
            bindSubmitForm={() => {}}
          />
          <FamilyData
            icon="fa-search"
            eventKey="3"
            data={caseRecord}
            disabledAll={true}
            bindSubmitForm={() => {}}
            required={false}
          />
          <MedicDataTreating
            icon="fa-search"
            eventKey="4"
            data={caseRecord}
            disabledAll={true}
            bindSubmitForm={() => {}}
          />
          <InsuranceData
            icon="fa-search"
            eventKey="6"
            data={caseRecord}
            disabledAll={true}
            bindSubmitForm={() => {}}
          />
          <Opinion
            icon="fa-search"
            eventKey="7"
            data={caseRecord}
            disabledAll={true}
            totalLetters={totalLetters}
            bindSubmitForm={() => {}}
          />
          <MedicalFees
            icon="fa-search"
            eventKey="10"
            data={caseRecord}
            hideBtnInsurance={true}
            hideFormCreate={true}
            showForm={false}
            {...props}
          />
          <AuthLetters
            icon="fa-search"
            showForm={false}
            hideDeleteOption={true}
            eventKey="8"
            data={caseRecord}
            disabledAll={true}
            setTotalLetters={setTotalLetters}
            {...props}
          />
          <Documentation
            icon="fa-search"
            eventKey="9"
            data={caseRecord}
            disabledAll={true}
            {...props}
          />
          <ConciergeFollowUp icon="fa-pencil-alt" eventKey="12" data={caseRecord} />
        </Accordion>
        <Accordion defaultActiveKey="11" className="mt-5">
          <Binnacle
            icon="fa-pencil-alt"
            eventKey="11"
            data={caseRecord}
            disabledAll={false}
            hideButton={false}
            eventStage="Concierge"
            {...props}
          />
        </Accordion>
      </BlockCaseContext.Provider>

      <Row>
        <Col md={12} className="mt-5">
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Motivo (obligatorio para regresar el caso a otra estación)"
            value={reprocessReason}
            onChange={(e) => setReprocessReason(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mt-3">
          <Button variant="danger" onClick={onClickSendMedicalOpinion}>
            Enviar a Dictamen Médico
          </Button>
        </Col>
        <Col className="mt-3">
          <Button variant="danger" onClick={onClickSendControlDesk}>
            Enviar a Mesa de Control
          </Button>
        </Col>
        <Col className="mt-3">
          <Button variant="danger" onClick={onClickSendCostControl}>
            Enviar a Control de Costos
          </Button>
        </Col>
        <Col className="mt-3">
          <Button variant="success" onClick={onClickSendStorePatient}>
            Enviar a Alta Paciente
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default WithCommentValidation(ConciergeDetails);
